/**
 * The API client, and the types the console reads.
 *
 * Every shape here is derived from the backend source — `api/src/**\/*.controller.ts` and
 * `api/prisma/schema.prisma` — not from any previous frontend. Where a value is an enum, the
 * members are the schema's members in the schema's order, so a missing case is visible as a
 * missing case rather than as a silent fallthrough.
 *
 * There is no cross-origin call. Vite proxies `/api` to Nest in development and nginx does the
 * same in production, so the session cookie is first-party in both.
 */

const BASE = '/api'

export class ApiError extends Error {
  // Declared as fields and assigned in the body rather than as constructor parameter properties —
  // `erasableSyntaxOnly` is on in tsconfig, and parameter properties emit code rather than
  // erasing, so they are not allowed.
  readonly status: number
  readonly body?: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  /** A refused state transition, e.g. activating a domain whose DNS has not verified (§18). */
  get isConflict() {
    return this.status === 409
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(BASE + path, {
    method,
    // The session is an httpOnly cookie. Nothing reads a token in JavaScript, so nothing can
    // leak one.
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  const text = await response.text()
  const parsed: unknown = text ? JSON.parse(text) : null

  if (!response.ok) {
    // Nest's validation pipe returns `message` as an array of failures. Joining them keeps every
    // reason rather than showing the first and discarding the rest.
    const raw = (parsed as { message?: string | string[] } | null)?.message
    const message = Array.isArray(raw) ? raw.join('. ') : (raw ?? response.statusText)
    throw new ApiError(response.status, message, parsed)
  }

  return parsed as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
}

// ── identity (§56, P027) ──────────────────────────────────────────────────────

/** `api/src/auth/permissions.ts` → PERMISSIONS. */
export type Permission =
  | 'view_contacts'
  | 'import_contacts'
  | 'export_contacts'
  | 'edit_templates'
  | 'approve_messages'
  | 'manage_campaigns'
  | 'change_mailbox_limits'
  | 'connect_credentials'
  | 'pause_system'
  | 'manage_suppression'
  | 'view_raw_replies'
  | 'manage_domains'
  | 'view_audit_log'
  | 'run_dry_run'
  | 'operate_dispatch'

/** `schema.prisma` → OrgRole. What someone may do **inside one organization**. */
export type OrgRole =
  | 'SUPER_ADMIN'
  | 'OUTREACH_ADMIN'
  | 'CAMPAIGN_MANAGER'
  | 'REVIEWER'
  | 'OPERATOR'
  | 'ANALYST'
  | 'READ_ONLY'

/** `schema.prisma` → PlatformRole. Axmion staff standing. NONE for every customer. */
export type PlatformRole = 'NONE' | 'SUPPORT' | 'STAFF' | 'OWNER'

export interface Membership {
  organizationId: string
  organizationSlug: string
  organizationName: string
  role: OrgRole
  permissions: Permission[]
}

export interface CurrentUser {
  id: string
  email: string
  name: string
  platformRole: PlatformRole
  platformPermissions: string[]
  /** Null for a platform-only account, or one whose last membership was revoked. */
  membership: Membership | null
  /** Every organization this person could act in. One during the ICPK-only phase. */
  memberships: Membership[]
}

// ── system (§47, §49) ─────────────────────────────────────────────────────────

/**
 * Three values, not one. `enabled` is the effective answer — both tiers agreeing — and the other
 * two say which tier is responsible when it is off. A screen that shows only `enabled` cannot
 * tell an operator whether they paused themselves or Axmion stopped everyone.
 */
export interface OutboundStatus {
  enabled: boolean
  platform: boolean
  organization: boolean
}

/**
 * `GET /api/system/sender-identity` — §83.
 *
 * Who, in law, is sending the mail. It appears in every message footer, and **§30 refuses every
 * send while it is null** — so `null` here is a blocker to render as one, never an empty field.
 * P003 is the record of whose decision the wording is: the tenant's, not the platform's.
 */
export interface SenderIdentity {
  value: string | null
  required: boolean
}

/**
 * A §64 decision on everyone matching a filter — `POST :id/contacts/approve-all`.
 *
 * **`expectedCount` is the safety property**, not a hint. It is the number this screen displayed;
 * the API refuses the whole call with a 409 if the real set has changed since, and says both
 * numbers. Send what was rendered, never a fresh count fetched to make the call succeed — that
 * defeats the entire mechanism.
 */
export interface ReviewAllInput {
  approval: ApprovalStatus
  expectedCount: number
  reason?: string
}

/**
 * `GET /api/credentials` — secrets belonging to **this organization**, not to Axmion (P041).
 *
 * **There is no value field and there cannot be.** No endpoint returns a stored secret, so this
 * type carries the shape only: what kind, what it is called, when it was set, and whether it last
 * failed. A `lastFailureAt` is how a broken credential is visible without anyone decrypting it to
 * find out.
 */
export interface TenantCredential {
  kind: 'CLOUDFLARE_DNS' | 'GOOGLE_OAUTH_CLIENT' | 'MICROSOFT_OAUTH_CLIENT'
  label: string
  metadata: Record<string, unknown> | null
  updatedAt: string
  lastFailureAt: string | null
  lastFailureDetail: string | null
}

/**
 * `GET /api/campaigns/:id/contacts/:ccId/preview` — §64's review preview.
 *
 * **The message a reviewer is actually approving**, rendered by the dispatcher's own renderer in
 * §29's order and put through §30's preflight, plus the provenance behind the address. Nothing is
 * written: no claim, no schedule, no message row.
 *
 * `notes` carries what the preview cannot show and why — the placeholder unsubscribe link, the
 * missing personalization layer (§26 is Phase 6), a mailbox that could not send. Render them; they
 * are the difference between a preview and a promise.
 */
export interface RecipientPreview {
  campaignContactId: string
  approvalStatus: ApprovalStatus
  contact: {
    id: string
    email: string
    name: string | null
    title: string | null
    department: string | null
    institution: string | null
    country: string | null
    verification: string
    source: string | null
    sourceUrl: string | null
    capturedAt: string | null
    relevanceScore: number | null
  }
  mailbox: { id: string; email: string; displayName: string; state: string } | null
  message: {
    subject: string
    text: string
    html: string | null
    unresolved: string[]
    empty: string[]
    resolvedFrom: Record<string, string>
    templateVersionId: string
    templateName: string
  } | null
  preflight: {
    blocking: Array<{ code: string; severity: string; message: string }>
    warnings: Array<{ code: string; severity: string; message: string }>
  }
  suppressed: { suppressed: boolean; reason: string | null }
  notes: string[]
}

/**
 * `GET /api/webhooks` — §50's inbound gateway, platform plane.
 *
 * **The body is deliberately absent** and the API never sends it: §59's data minimisation applies
 * to a debugging screen exactly as it applies to a reply. `payloadHash` is §51's dedup key, which
 * answers "have we seen this exact delivery before" without carrying what it said.
 */
export interface WebhookDelivery {
  id: string
  source: string
  status: string
  receivedAt: string
  processedAt: string | null
  payloadHash: string | null
}

/** `GET /api/webhooks/sources` — what is arriving, and whether anything processes it. */
export interface WebhookSourceCount {
  source: string
  status: string
  count: number
  lastReceivedAt: string | null
}

/** `GET /api/health` — unauthenticated, and the **platform** tier only. */
export interface Health {
  status: 'ok' | 'degraded'
  checks: { database: boolean }
  platformOutboundEnabled: boolean
  timestamp: string
}

// ── domains (§13, §15, §18, §79) ──────────────────────────────────────────────

export type HealthState = 'HEALTHY' | 'WARNING' | 'BROKEN' | 'UNKNOWN'

export type DomainState =
  | 'PENDING'
  | 'CONFIGURING'
  | 'VERIFYING'
  | 'READY'
  | 'ACTIVE'
  | 'DEGRADED'
  | 'PAUSED'
  | 'RETIRED'

export type DomainPurpose =
  | 'CORE_ORGANIZATIONAL'
  | 'CORE_JOURNAL'
  | 'ACQUISITION_OUTREACH'
  | 'TRACKING'
  | 'UNSUBSCRIBE'

/** A stored check row. `checkType` is lowercase on the wire — `spf`, `dkim`, `dmarc`, `mx`. */
export interface DomainCheck {
  id: string
  checkType: string
  result: HealthState
  message: string
  checkedAt: string
  detail: { found: string | null; fix: string | null; notes: string[] } | null
}

export interface Domain {
  id: string
  fqdn: string
  purpose: DomainPurpose
  state: DomainState
  health: HealthState
  isProtected: boolean
  registrar: string | null
  dnsProvider: string | null
  dailyLimit: number | null
  createdAt: string
  activatedAt: string | null
  checks: DomainCheck[]
  _count: { mailboxes: number }
}

/** A live lookup, as opposed to a stored `DomainCheck`. Same states, different source. */
export interface AuthCheck {
  name: 'SPF' | 'DKIM' | 'DMARC' | 'MX'
  state: HealthState
  found: string | null
  summary: string
  fix?: { type: string; host: string; value: string } | null
  notes?: string[]
}

export interface AuthReport {
  fqdn: string
  overall: HealthState
  checks: AuthCheck[]
  checkedAt: string
}

export interface VerifyResult {
  verification: AuthReport
}

/** Whether the records can be published for the operator, and if not, why not. */
export interface DnsCapability {
  automatic: boolean
  reason: string
  message: string
}

// ── contacts (§13, §62, §95) ──────────────────────────────────────────────────

export type VerificationStatus =
  | 'UNVERIFIED'
  | 'SYNTAX_OK'
  | 'DOMAIN_OK'
  | 'MX_OK'
  | 'VERIFIED'
  | 'INVALID'
  | 'RISKY'

/** A contact's university or employer. Called `Institution` since P027 took the other word. */
export interface Institution {
  canonicalName: string
  country: string | null
}

export interface Contact {
  id: string
  canonicalEmail: string
  firstName: string | null
  lastName: string | null
  countryCode: string | null
  verificationStatus: VerificationStatus
  institution: Institution | null
  createdAt: string
}

export interface ContactPage {
  items: Contact[]
  total: number
  take: number
  skip: number
}

export interface ImportOutcome {
  created: number
  updated: number
  rejected: Array<{ input: string; why: string }>
}

// ── suppression (§32, §57) ────────────────────────────────────────────────────

export type SuppressionScope = 'CAMPAIGN' | 'JOURNAL' | 'ORG_GLOBAL'

export type SuppressionReason =
  | 'UNSUBSCRIBED'
  | 'SPAM_COMPLAINT'
  | 'HARD_BOUNCE'
  | 'NOT_INTERESTED'
  | 'LEGAL_RESTRICTION'
  | 'MANUAL_SUPPRESSION'
  | 'EXCESSIVE_PREVIOUS_CONTACT'
  | 'INVALID_ADDRESS'
  | 'ABUSE_CONTACT'

export interface SuppressionEntry {
  id: string
  emailNormalized: string
  scope: SuppressionScope
  reason: SuppressionReason
  source: string
  suppressedAt: string
  expiresAt: string | null
}

export interface SuppressionCheck {
  suppressed: boolean
  reason: SuppressionReason | null
  scope: SuppressionScope | null
  entryId: string | null
}

// ── a page of anything ────────────────────────────────────────────────────────

/**
 * Every list endpoint added on 2026-08-20 returns this shape. Declared once so a screen that
 * forgets `total` cannot quietly claim to be showing everything — the failure P026 is about.
 */
export interface Page<T> {
  items: T[]
  total: number
  take: number
  skip: number
}

// ── audit (§57) ───────────────────────────────────────────────────────────────

export interface AuditActor {
  id: string
  email: string
  name: string
}

export interface AuditEntry {
  id: string
  action: string
  objectType: string
  objectId: string | null
  oldValue: unknown
  newValue: unknown
  reason: string | null
  ipAddress: string | null
  createdAt: string
  /** Null for an action taken by nobody — a failed login, or the system itself. */
  user: AuditActor | null
}

export interface AuditActionCount {
  action: string
  count: number
}

// ── campaigns (§45, §81, §99) ─────────────────────────────────────────────────

/** `schema.prisma` → CampaignState. There is no SCHEDULED here; that belongs to MessageState. */
export type CampaignState =
  | 'DRAFT'
  | 'VALIDATING'
  | 'AWAITING_APPROVAL'
  | 'READY'
  | 'RUNNING'
  | 'PAUSING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'ARCHIVED'
  | 'FAILED'

export interface Campaign {
  id: string
  name: string
  state: CampaignState
  /** §99. True until someone deliberately turns it off, which is its own audited act. */
  dryRun: boolean
  dailyLimit: number | null
  requiresApproval: boolean
  scheduledStartAt: string | null
  startedAt: string | null
  completedAt: string | null
  pausedAt: string | null
  pausedReason: string | null
  createdAt: string
  journal: { id: string; name: string } | null
  _count: { campaignContacts: number; outboundMessages: number }
}

export interface CampaignEvent {
  id: string
  type: string
  /**
   * §6's free-form event body. `unknown` because each type carries a different shape and a
   * union of eighteen of them would be a lie maintained by hand.
   *
   * One key is worth reading and is read by the campaign screen: `detail`, written by every
   * `MESSAGE_REFUSED` event, is the sentence explaining why one recipient was not sent to
   * (P046). Use `eventDetail()` rather than reaching into this directly.
   */
  payload: unknown
  actorId: string | null
  createdAt: string
}

/**
 * The human-readable half of an event, when it has one.
 *
 * Narrowing rather than casting: a payload arrives as JSON from the database and nothing
 * guarantees its shape, so anything that is not a string `detail` reads as absent.
 */
export function eventDetail(payload: unknown): string | null {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail: unknown }).detail
    if (typeof detail === 'string' && detail.length > 0) {
      return detail
    }
  }
  return null
}

/**
 * What actually happened, for an event whose type cannot say.
 *
 * **`EventType` offers exactly one value for every campaign transition — `CAMPAIGN_PAUSED`** — so a
 * campaign that was just *started* is recorded, and was until 2026-08-25 *displayed*, as paused.
 * The service writes `from`/`to` into the payload precisely because the type cannot carry it;
 * nothing read them, and the screen printed the raw type beside a campaign that was running.
 *
 * That is the P046 shape again, in the same list, three lines from the comment describing P046.
 * Rendering the transition is the half that needs no migration; whether the enum should gain a
 * `CAMPAIGN_STATE_CHANGED` value is
 * [P062](../../docs/problems/p062-one-event-type-for-every-transition.md) and is the owner's call.
 */
export function eventTransition(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const { from, to } = payload as { from?: unknown; to?: unknown }
  if (typeof from === 'string' && typeof to === 'string') {
    return `${from} → ${to}`
  }
  return null
}

export interface CampaignDetail extends Campaign {
  events: CampaignEvent[]
}

/** §81. `blocking` is what stops it now; `pending` is what is not checked at all yet. */
export interface Readiness {
  campaignId: string
  state: CampaignState
  dryRun: boolean
  approvedCount: number
  blocking: string[]
  ready: boolean
  pending: string[]
}

// ── campaign recipients and §64's review queue ────────────────────────────────

/** `schema.prisma` → ApprovalStatus. `NOT_REQUIRED` is a campaign with review turned off. */
export type ApprovalStatus = 'NOT_REQUIRED' | 'PENDING' | 'APPROVED' | 'REJECTED'

/** `schema.prisma` → CampaignContactStatus. Where one recipient has got to (§45). */
export type CampaignContactStatus =
  | 'PENDING'
  | 'QUALIFYING'
  | 'AWAITING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'REPLIED'
  | 'COMPLETED'
  | 'STOPPED'
  | 'SUPPRESSED'

/**
 * One row of `campaign_contacts` — a person's membership of a campaign, not the person.
 *
 * `id` is the membership's id and it is what the review endpoints take. The contact's own id is
 * nested, because the same contact can be in two campaigns and approved for only one.
 */
export interface CampaignRecipient {
  id: string
  status: CampaignContactStatus
  approvalStatus: ApprovalStatus
  personalizationStatus: string
  sequenceStep: number
  /** §22. Null until the planner materializes a send time — approval alone does not schedule. */
  nextActionAt: string | null
  mailboxId: string | null
  createdAt: string
  contact: {
    id: string
    canonicalEmail: string
    firstName: string | null
    lastName: string | null
  }
}

/**
 * A page of recipients, plus the whole campaign's approval counts.
 *
 * The counts are not derivable from `items` — a reviewer looking at 100 of 4,000 rows needs to
 * know what is left, and every member of the enum is present including the zeroes.
 */
export interface CampaignRecipientPage extends Page<CampaignRecipient> {
  counts: Record<ApprovalStatus, number>
}

/** `POST /campaigns/:id/contacts`. `unknown` names the ids that resolved to nothing. */
export interface AddContactsResult {
  added: number
  alreadyPresent: number
  unknown: string[]
  nextStep: string
}

/**
 * `POST /campaigns/:id/contacts/approve` and `…/reject`.
 *
 * `dequeued` appears only on a rejection that actually took something out of the dispatcher's
 * queue. It is the one number here that describes mail which would otherwise have gone out.
 */
export interface ReviewResult {
  decided: number
  alreadyDecided: number
  unknown: string[]
  reversals: number
  dequeued?: number
  warning?: string
  nextStep: string
}

// ── templates (§28, §64) ──────────────────────────────────────────────────────

export interface TemplateVersion {
  id: string
  version: number
  subjectTemplate: string
  approvedAt: string | null
  createdAt: string
}

/**
 * A version with its wording, as `GET /templates/:id` returns it.
 *
 * Separate from `TemplateVersion` because the list endpoint deliberately does not carry bodies —
 * every version of every template would grow without bound on a list screen.
 */
export interface TemplateVersionFull extends TemplateVersion {
  textBody: string
  htmlBody: string | null
  footer: string | null
  approvedById: string | null
}

/** One entry in the vocabulary from `GET /templates/variables` (§116 item 40). */
export interface VariableDefinition {
  name: string
  layer: 'journal' | 'contact' | 'personalization' | 'unsubscribe' | 'sender'
  bare: string
  /** Nullable at source. §30 refuses a message whose personalization rendered blank. */
  optional: boolean
  description: string
}

/** Shared with §30's preflight, deliberately — one severity vocabulary across both checks. */
export interface VariableFinding {
  code: string
  severity: 'BLOCK' | 'WARN' | 'NOT_CHECKED'
  message: string
}

export interface VariableValidation {
  findings: VariableFinding[]
  blocked: boolean
  used: { placeholder: string; resolves: string | null; optional: boolean | null }[]
}

export interface Template {
  id: string
  name: string
  family: string | null
  language: string
  tone: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  journal: { id: string; name: string } | null
  _count: { versions: number }
  /** The latest version only, as one element. §28: a version is never edited, only added to. */
  versions: TemplateVersion[]
}

// ── mailboxes (§20, §21, §54) ─────────────────────────────────────────────────

/**
 * These three were **all wrong** until 2026-08-21, and every one of them was invented rather than
 * read off `schema.prisma`. Nothing failed, because nothing has ever returned a real value for
 * them — no mailbox is connected. The first connected mailbox would have rendered an undefined
 * label and an undefined tone, which is a blank cell, not an error.
 *
 * Checked against `api/prisma/schema.prisma` and corrected. What was wrong:
 *
 *   MailboxState     had CONNECTED / WARMING / THROTTLED / FAILED — none exist. The real ones are
 *                    AUTHORIZING, VALIDATING, READY, COOLDOWN, DEGRADED, AUTH_FAILED, DISABLED
 *   ProviderKind     had GMAIL, SMTP, OTHER. It is GOOGLE and SMTP_IMAP, and there is no OTHER
 *   CredentialState  had EXPIRED and FAILED. §55's are REAUTH_REQUIRED and ERROR — and the
 *                    difference matters, because REAUTH_REQUIRED means a human must act and no
 *                    amount of retrying will help
 */
export type MailboxState =
  | 'NEW'
  | 'AUTHORIZING'
  | 'VALIDATING'
  | 'READY'
  | 'ACTIVE'
  | 'COOLDOWN'
  | 'PAUSED'
  | 'DEGRADED'
  | 'AUTH_FAILED'
  | 'DISABLED'
  | 'RETIRED'

export type ProviderKind = 'GOOGLE' | 'MICROSOFT' | 'ZOHO' | 'SMTP_IMAP'

export type CredentialState =
  | 'CONNECTED'
  | 'EXPIRING'
  | 'REAUTH_REQUIRED'
  | 'REVOKED'
  | 'ERROR'

// ── connecting a provider (§9, §55; build items 11, 12, 16, 17) ───────────────

/** `GET /providers`. Health is derived on every read, never stored — see `provider-health.service.ts`. */
export interface ProviderHealth {
  id: string
  kind: ProviderKind
  label: string
  externalAccountId: string | null
  state: CredentialState
  verdict: 'OK' | 'DEGRADED' | 'BLOCKED'
  /** Always populated, including when the verdict is OK. Written for an operator, not a log. */
  reasons: string[]
  canSend: boolean
  hasCredential: boolean
  adapterBuilt: boolean
  lastRefreshAt: string | null
  lastFailureAt: string | null
  lastFailureDetail: string | null
  mailboxes: { total: number; sending: number }
}

export interface ProviderList {
  accounts: ProviderHealth[]
  implemented: ProviderKind[]
  notImplemented: ProviderKind[]
}

/**
 * What the callback puts in the query string on the way back.
 *
 * `norefresh` is the one worth handling separately: Google issued an access token but no refresh
 * token, so the account was **not** stored. It is not a failure of the connection — it is a
 * connection that would have died within the hour, refused on purpose.
 */
export type ConnectOutcome = 'ok' | 'declined' | 'failed' | 'norefresh'

export interface Mailbox {
  id: string
  email: string
  displayName: string | null
  state: MailboxState
  dailyLimit: number
  hourlyLimit: number | null
  campaignLimit: number | null
  minimumGapSec: number
  maximumGapSec: number
  sentToday: number
  timezone: string
  healthScore: number | null
  lastSuccessAt: string | null
  consecutiveFailures: number
  createdAt: string
  domain: { id: string; fqdn: string; state: DomainState; health: HealthState }
  providerAccount: { id: string; kind: ProviderKind; label: string; state: CredentialState }
}

// ── replies (§36, §37, §59) ───────────────────────────────────────────────────

export type ReplyClass =
  | 'POSITIVE'
  | 'NEGATIVE'
  | 'NEUTRAL'
  | 'OUT_OF_OFFICE'
  | 'AUTO_REPLY'
  | 'BOUNCE'
  | 'UNSUBSCRIBE_REQUEST'
  | 'COMPLAINT'
  | 'UNCLASSIFIED'

export interface Reply {
  id: string
  fromEmail: string
  subject: string | null
  replyClass: ReplyClass | null
  classifierModel: string | null
  classifierConfidence: number | null
  /** Set when a person disagreed with the classifier. Both verdicts are kept (§36). */
  overriddenById: string | null
  oooReturnDate: string | null
  receivedAt: string
  threadId: string | null
  contact: {
    id: string
    canonicalEmail: string
    firstName: string | null
    lastName: string | null
  } | null
  mailbox: { id: string; email: string } | null
}

export interface ReplyClassCount {
  replyClass: ReplyClass | null
  count: number
}

// ── deliverability (§16, §35, §43) ────────────────────────────────────────────

export type BounceClass = 'HARD' | 'SOFT' | 'BLOCK' | 'SPAM' | 'POLICY' | 'TECHNICAL' | 'UNKNOWN'

/**
 * §43 forbids a single universal reputation score, so there is no overall grade in this shape —
 * and one must not be computed from it in the browser either. Sources stay separately attributed.
 */
export interface Deliverability {
  windowDays: number
  since: string
  domains: Array<{
    id: string
    fqdn: string
    state: DomainState
    health: HealthState
    purpose: DomainPurpose
    _count: { mailboxes: number }
  }>
  bounces: Array<{ bounceClass: BounceClass; count: number }>
  complaints: number
  signals: Array<{
    id: string
    source: string
    metric: string
    value: number | null
    windowDate: string
    domainId: string | null
  }>
  checks: Array<{
    id: string
    domainId: string
    checkType: string
    result: HealthState
    message: string | null
    checkedAt: string
  }>
  /** Which of §43's sources actually write anything today, and what blocks the rest. */
  sources: Array<{ source: string; live: boolean; note: string }>
}

// ── schedules (§21, §22, §33) ─────────────────────────────────────────────────

/**
 * The inputs to §21's allowance, never the allowance itself — that is resolved at send time across
 * six layers and this API cannot compute it. `resolvedAtSendTime` says so on the wire.
 */
export interface Schedules {
  mailboxes: Array<{
    id: string
    email: string
    state: MailboxState
    timezone: string
    workingDays: unknown
    workingWindow: unknown
    dailyLimit: number
    hourlyLimit: number | null
    campaignLimit: number | null
    minimumGapSec: number
    maximumGapSec: number
    sentToday: number
    sentTodayResetAt: string | null
    domain: { id: string; fqdn: string; dailyLimit: number | null }
    providerAccount: { id: string; kind: ProviderKind; label: string }
  }>
  policies: Array<{
    id: string
    providerAccountId: string
    accountType: string | null
    technicalQuota: number | null
    organizationQuota: number | null
    policyReviewedAt: string | null
    providerAccount: { kind: ProviderKind; label: string }
  }>
  campaigns: Array<{
    id: string
    name: string
    state: CampaignState
    dailyLimit: number | null
    scheduledStartAt: string | null
    dryRun: boolean
  }>
  queue: {
    pendingActions: number
    nextActionAt: string | null
    /** True once a scheduler is sweeping. `GET /sending/status` is the authority on this. */
    dispatcherRunning: boolean
  }
  resolvedAtSendTime: true
}

// ── the sending engine (§21, §22, §23, §52, §99) ─────────────────────────────

/** `api/src/generated/prisma/enums.ts` → MessageState. §46's delivery lifecycle. */
export type MessageState =
  | 'DRAFT'
  | 'PERSONALIZING'
  | 'AWAITING_APPROVAL'
  | 'SCHEDULED'
  | 'QUEUED'
  | 'CLAIMED'
  | 'SENDING'
  | 'SENT'
  | 'ACCEPTED'
  | 'TEMP_FAILED'
  | 'PERM_FAILED'
  | 'BOUNCED'
  | 'REPLIED'
  | 'CANCELLED'
  | 'SUPPRESSED'

export interface TickResult {
  startedAt: string
  durationMs: number
  organizations: Array<{
    organizationId: string
    name: string
    planned: number
    sent: number
    refused: number
    deferred: number
    failed: number
  }>
  skipped: string[]
}

export interface SendingStatus {
  scheduler: {
    enabled: boolean
    running: boolean
    /**
     * How the sweep is driven. `timer` is this process and needs no Redis — what production runs,
     * per P054; `redis` is §24's BullMQ queue, opt-in for a second box or real volume.
     */
    driver: 'timer' | 'redis'
    intervalMs: number
    worker: string
    lastTick: TickResult | null
  }
  queue: {
    due: number
    scheduled: number
    inProgress: number
    nextActionAt: string | null
    /**
     * §24's Redis queue, as opposed to the database's. **Null means the queue runtime is off**,
     * which is a different answer from zero and has to render as one — "not running" rather than
     * "nothing waiting".
     */
    redis: {
      waiting: number
      active: number
      failed: number
      delayed: number
    } | null
    /** How many recipients one process sends at once. Null when the runtime is off. */
    concurrency: number | null
  }
  messages: Partial<Record<MessageState, number>>
  deadLetters: {
    total: number
    byQueue: Array<{ queue: string; unresolved: number; lastAttemptAt: string | null }>
  }
  /**
   * §99. True while every send goes through the null provider.
   *
   * Counted from the campaigns rather than asserted — it was hardcoded `true` in the API for two
   * days after the Gmail adapter landed, including through the first real send (P053).
   */
  allSendsAreDryRuns: boolean
}

/** `api/src/sending/quota.ts` → QuotaResolution. §21, resolved and explainable. */
export interface QuotaResolution {
  allowed: boolean
  remaining: number
  effective: {
    source: string
    window: string
    limit: number
    remaining: number
  } | null
  rules: Array<{
    source: string
    window: string
    limit: number
    used: number
    remaining: number
    /** False for the provider's technical ceiling, which §21 forbids treating as an allowance. */
    applies: boolean
    detail: string
  }>
  blocks: Array<{ source: string; reason: string; clearsAt: string | null }>
  nextEligibleAt: string | null
  resolvedAt: string
}

export interface PlanResult {
  campaignId: string
  considered: number
  scheduled: number
  suppressed: number
  skipped: Array<{ reason: string; count: number }>
  firstActionAt: string | null
}

// ── API keys (build item 59) ─────────────────────────────────────────────────

export interface ApiKey {
  id: string
  name: string
  /** The public half. `mr_live_<16 hex>`. The other half exists nowhere but the customer's. */
  prefix: string
  scopes: Permission[]
  lastUsedAt: string | null
  lastUsedIp: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string
  createdBy: { id: string; name: string } | null
  revokedBy: { id: string; name: string } | null
  /** Computed by the API, not stored — a key does not change when its expiry passes. */
  status: 'active' | 'never used' | 'expired' | 'revoked'
}

export interface ApiKeyScopes {
  available: Array<{ permission: Permission; grantable: boolean }>
  forbidden: Array<{ permission: Permission; reason: string }>
}

/**
 * The **only** response in this API that contains a key.
 *
 * `token` exists here and nowhere else — not in the list, not in the audit trail, not in the
 * database. Whatever holds this object is holding the single copy.
 */
export interface MintedApiKey {
  id: string
  name: string
  prefix: string
  scopes: Permission[]
  refusedScopes: string[]
  expiresAt: string | null
  createdAt: string
  token: string
  warning: string
}

export interface DeadLetterJob {
  id: string
  queue: string
  jobType: string
  objectType: string | null
  objectId: string | null
  attempt: number
  failure: string
  lastAttemptAt: string
  replayedAt: string | null
  resolvedAt: string | null
}
